import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { apiSlice } from "../../../features/api/apiSlice";
import { useParams, useNavigate } from "react-router-dom";
import {
  Users,
  RefreshCw,
  ChevronLeft,
  Calendar,
  Trash2,
  Mail,
  Phone,
  ArrowRightLeft,
  ShieldCheck,
  ShieldAlert,
  Crown,
  MapPin,
  Building2,
  Plus,
  ChevronDown,
  ChevronRight,
  Search,
  UserPlus,
  Pencil,
  X,
  Check,
  UserMinus,
} from "lucide-react";

// ✅ Hooks & Utils
import { PERMS } from "../../../utils/perms";
import AccessGate from "../../../components/AccessGate";
import PageGate from "../../../components/PageGate";
import { useAccess } from "../../../utils/useAccess";

import AssignExecutiveToSet from "../../executiveOffices/components/AssignExecutiveToSet";
// import AssignNecToSet from "../../necOffice/components/AssignNecToSet";

import {
  useGetExecutiveOfficesQuery,
  useMoveExecutiveMutation,
  useRemoveExecutiveMutation,
} from "../../executiveOffices/api/executiveOfficeApi";

import {
  useGetNecOfficesQuery,
  useMoveNecMutation,
  useRemoveNecMutation,
} from "../../necOffice/api/necOfficeApi";

import { useGetExecutiveSetDetailsQuery } from "../api/executiveSetApi";
import {
  useGetCommitteesBySetQuery,
  useCreateSetCommitteeMutation,
  useUpdateSetCommitteeMutation,
  useDeleteSetCommitteeMutation,
  useGetSetCommitteeMembersQuery,
  useAddSetCommitteeMemberMutation,
  useRemoveSetCommitteeMemberMutation,
  useLazySearchMembersForCommitteeQuery,
  type Committee,
  type MemberSearchResult,
} from "../api/executiveCommitteeApi";

function fullName(m: any) {
  return (
    [m?.surname, m?.firstname, m?.othernames].filter(Boolean).join(" ") || "—"
  );
}

const COMMITTEE_TYPES = [
  "Management Committee",
  "MemberShip Committee",
  "Education and Training Committee",
  "Finance  Committee",
  "Professional Development and Technical Committee",
  "Marketing and Corporate Affairs Committee",
  "Examination Board",
] as const;

const PAST_PRESIDENT_COMMITTEE_TYPES = [
  "Ethics and Practice Committee",
  "Disciplinary Board",
  "Committee of Past Presidents",
] as const;

const PP_TYPE_SET = new Set<string>(PAST_PRESIDENT_COMMITTEE_TYPES);

// ── CommitteeCard ────────────────────────────────────────────────────────────

type CommitteeMsgState = {
  id: number;
  type: "success" | "error";
  text: string;
} | null;

type CommitteeCardProps = {
  committee: Committee;
  setId: number;
  executives: any[];
  expanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  addingMemberOpen: boolean;
  onToggleAddMember: () => void;
  memberSearch: string;
  onMemberSearch: (q: string) => void;
  memberResults: MemberSearchResult[];
  searchingMembers: boolean;
  selectedMember: { id: string; label: string } | null;
  onSelectMember: (m: { id: string; label: string } | null) => void;
  newMemberRole: "chairman" | "secretary" | "member";
  onRoleChange: (r: "chairman" | "secretary" | "member") => void;
  newMemberPosition: string;
  onPositionChange: (p: string) => void;
  onAddMember: () => Promise<void>;
  addingMember: boolean;
  onRemoveMember: (memberId: string, name: string, action: "drop" | "delete") => void;
  committeeMsg: CommitteeMsgState;
  roleBadge: (role: string) => JSX.Element;
};

function CommitteeCard({
  committee,
  expanded,
  onToggle,
  onDelete,
  addingMemberOpen,
  onToggleAddMember,
  memberSearch,
  onMemberSearch,
  memberResults,
  searchingMembers,
  selectedMember,
  onSelectMember,
  newMemberRole,
  onRoleChange,
  newMemberPosition,
  onPositionChange,
  onAddMember,
  addingMember,
  onRemoveMember,
  committeeMsg,
  roleBadge,
}: CommitteeCardProps) {
  const { data: memberData, isLoading: loadingMembers, refetch: refetchMembers } =
    useGetSetCommitteeMembersQuery(committee.id, { skip: !expanded });
  const members = memberData?.members ?? [];

  const chairmanName = committee.chairman?.member
    ? fullName(committee.chairman.member)
    : null;

  // Deleting a committee takes every membership record with it, so the button
  // is offered only while the committee is empty. `members_count` is the
  // server's count of members still serving (removed_at is null); once the card
  // is expanded the loaded rows are the fresher answer, so both are checked.
  const hasMembers =
    (committee.members_count ?? 0) > 0 ||
    members.some((m: any) => !m.removed_at);

  return (
    <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
      {/* Card header row */}
      <div
        className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <div
            className={`w-2 h-2 rounded-full flex-shrink-0 ${
              committee.is_active ? "bg-emerald-500" : "bg-gray-300"
            }`}
          />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-black text-gray-900 text-sm">
                {committee.name}
              </span>
              {!committee.is_active && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-gray-100 text-gray-500 uppercase tracking-wider">
                  Inactive
                </span>
              )}
            </div>
            {committee.description && (
              <p className="text-xs text-gray-400 mt-0.5">
                {committee.description}
              </p>
            )}
            <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400 font-medium">
              {chairmanName && (
                <span className="flex items-center gap-1">
                  <Crown className="w-3 h-3 text-amber-500" />
                  {chairmanName}
                </span>
              )}
              <span>{committee.members_count ?? 0} member(s)</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!hasMembers && (
            <AccessGate perm={PERMS.executive_set?.update}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                title="Delete committee"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </AccessGate>
          )}
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Expanded panel */}
      {expanded && (
        <div className="border-t">
          {/* Inline feedback */}
          {committeeMsg && (
            <div
              className={`px-5 py-2.5 text-xs font-medium border-b ${
                committeeMsg.type === "success"
                  ? "bg-green-50 text-green-800 border-green-100"
                  : "bg-red-50 text-red-800 border-red-100"
              }`}
            >
              {committeeMsg.text}
            </div>
          )}

          {/* Members table */}
          <div className="px-5 py-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                Members
              </span>
              <AccessGate perm={PERMS.executive_set?.update}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleAddMember();
                  }}
                  className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-emerald-600 hover:text-emerald-800 transition-colors"
                >
                  <UserPlus className="w-3 h-3" />
                  {addingMemberOpen ? "Cancel" : "Add Member"}
                </button>
              </AccessGate>
            </div>

            {loadingMembers ? (
              <p className="text-xs text-gray-400 animate-pulse py-2">
                Loading members…
              </p>
            ) : members.length === 0 ? (
              <p className="text-xs text-gray-400 py-2">No members yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                      <th className="pb-2 text-left font-black">Name</th>
                      <th className="pb-2 text-left font-black">Role</th>
                      <th className="pb-2 text-left font-black">Position</th>
                      <th className="pb-2 text-right font-black"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {members.map((m: any) => {
                      const isRemoved = !!m.removed_at;
                      return (
                        <tr
                          key={m.id}
                          className={
                            isRemoved
                              ? "bg-gray-50/80 opacity-60"
                              : "hover:bg-gray-50/50 transition-colors"
                          }
                        >
                          <td className="py-2.5 pr-4">
                            <div
                              className={`text-xs font-medium ${
                                isRemoved
                                  ? "line-through text-gray-400"
                                  : "text-gray-800"
                              }`}
                            >
                              {fullName(m.member)}
                            </div>
                            {m.member?.membership_no && (
                              <div className="text-[10px] text-gray-400 font-mono">
                                {m.member.membership_no}
                              </div>
                            )}
                            {isRemoved && (
                              <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider bg-gray-200 text-gray-500">
                                Dropped
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 pr-4">
                            {roleBadge(m.committee_role ?? "member")}
                          </td>
                          <td className="py-2.5 pr-4 text-xs text-gray-500">
                            {m.position || "—"}
                          </td>
                          <td className="py-2.5 text-right">
                            {!isRemoved && (
                              <AccessGate perm={PERMS.executive_set?.update}>
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={() =>
                                      onRemoveMember(
                                        m.member_id,
                                        fullName(m.member),
                                        "drop",
                                      )
                                    }
                                    className="p-1 rounded text-amber-500 hover:text-amber-700 hover:bg-amber-50 transition-colors"
                                    title="Drop from committee (keeps record for reference)"
                                  >
                                    <UserMinus className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={() =>
                                      onRemoveMember(
                                        m.member_id,
                                        fullName(m.member),
                                        "delete",
                                      )
                                    }
                                    className="p-1 rounded text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                    title="Delete record (use only if added in error)"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </AccessGate>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Add-member form */}
          {addingMemberOpen && (
            <div className="border-t px-5 py-4 bg-gray-50/60 space-y-3">
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                Add New Member
              </p>

              {/* Live search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                <input
                  value={memberSearch}
                  onChange={(e) => onMemberSearch(e.target.value)}
                  placeholder="Search by name or membership no. (min 3 chars)"
                  className="w-full border rounded-lg pl-9 pr-3 py-2 text-xs outline-none focus:ring-2 focus:ring-gray-200 bg-white"
                />
                {searchingMembers && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                )}
              </div>

              {/* Results list */}
              {memberResults.length > 0 && !selectedMember && (
                <div className="border rounded-lg bg-white shadow-sm divide-y max-h-40 overflow-y-auto">
                  {memberResults.map((r) => {
                    const label =
                      [r.surname, r.firstname, r.othernames]
                        .filter(Boolean)
                        .join(" ") +
                      (r.membership_no ? ` (${r.membership_no})` : "");
                    return (
                      <button
                        key={r.id}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-emerald-50 transition-colors"
                        onClick={() => onSelectMember({ id: r.id, label })}
                      >
                        <span className="font-medium">{label}</span>
                        {r.email && (
                          <span className="text-gray-400 ml-2">{r.email}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Selected member pill */}
              {selectedMember && (
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs">
                  <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                  <span className="font-bold text-emerald-800 flex-1">
                    {selectedMember.label}
                  </span>
                  <button
                    onClick={() => onSelectMember(null)}
                    className="text-emerald-500 hover:text-emerald-700"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {/* Role + position */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 block mb-1">
                    Role
                  </label>
                  <select
                    value={newMemberRole}
                    onChange={(e) =>
                      onRoleChange(
                        e.target.value as "chairman" | "secretary" | "member",
                      )
                    }
                    className="w-full border rounded-lg px-3 py-2 text-xs bg-white outline-none focus:ring-2 focus:ring-gray-200"
                  >
                    <option value="member">Member</option>
                    <option value="secretary">Secretary</option>
                    <option value="chairman">Chairman</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 block mb-1">
                    Position (optional)
                  </label>
                  <input
                    value={newMemberPosition}
                    onChange={(e) => onPositionChange(e.target.value)}
                    placeholder="e.g. Finance Officer"
                    className="w-full border rounded-lg px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-gray-200 bg-white"
                  />
                </div>
              </div>

              <button
                onClick={async () => {
                  await onAddMember();
                  refetchMembers();
                }}
                disabled={!selectedMember || addingMember}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-xs font-black hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                <UserPlus className="w-3.5 h-3.5" />
                {addingMember ? "Adding…" : "Add to Committee"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ExecutiveSetDetailsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const setId = Number(id);
  const { can } = useAccess();

  const { data, isLoading, error, refetch } = useGetExecutiveSetDetailsQuery(
    setId,
    { skip: !setId },
  );

  // ===== API Hooks =====
  const { data: execOffices = [] } = useGetExecutiveOfficesQuery();
  const [removeExec, { isLoading: removingExec }] =
    useRemoveExecutiveMutation();
  const [moveExec, { isLoading: movingExec }] = useMoveExecutiveMutation();

  const { data: necOffices = [] } = useGetNecOfficesQuery();
  const [removeNec, { isLoading: removingNec }] = useRemoveNecMutation();
  const [moveNec, { isLoading: movingNec }] = useMoveNecMutation();

  // ===== State =====
  const [msg, setMsg] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // ── Committee hooks ──────────────────────────────────────────────────────
  const { data: committees = [], refetch: refetchCommittees } =
    useGetCommitteesBySetQuery(setId, { skip: !setId });
  const [createCommittee, { isLoading: creatingCommittee }] =
    useCreateSetCommitteeMutation();
  const [updateCommittee] = useUpdateSetCommitteeMutation();
  const [deleteCommittee] = useDeleteSetCommitteeMutation();
  const [addMember, { isLoading: addingMember }] =
    useAddSetCommitteeMemberMutation();
  const [removeMember] = useRemoveSetCommitteeMemberMutation();
  const [
    searchMembers,
    { data: memberResults = [], isFetching: searchingMembers },
  ] = useLazySearchMembersForCommitteeQuery();

  // ── Committee UI state ────────────────────────────────────────────────────
  const [showCreateCommittee, setShowCreateCommittee] = useState(false);
  const [newCommittee, setNewCommittee] = useState({
    name: "",
    description: "",
    committee_type: "executive_set" as "executive_set" | "open",
    chairman_executive_id: "" as string | number,
  });
  const [showCreatePpCommittee, setShowCreatePpCommittee] = useState(false);
  const [newPpCommittee, setNewPpCommittee] = useState({
    name: "",
    description: "",
  });
  const [expandedCommittee, setExpandedCommittee] = useState<number | null>(
    null,
  );
  const [addingMemberToCommittee, setAddingMemberToCommittee] = useState<
    number | null
  >(null);
  const [memberSearch, setMemberSearch] = useState("");
  const [selectedMember, setSelectedMember] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const [newMemberRole, setNewMemberRole] = useState<
    "chairman" | "secretary" | "member"
  >("member");
  const [newMemberPosition, setNewMemberPosition] = useState("");
  const [committeeMsg, setCommitteeMsg] = useState<CommitteeMsgState>(null);

  // ===== Data Processing =====
  const executives = useMemo(() => data?.executives || [], [data]);
  console.log(executives, "tjhese are the exective*****************");
  const necs = useMemo(() => data?.necs || [], [data]);

  const existingExecMemberIds = useMemo(
    () => executives.map((e: any) => e.member_id),
    [executives],
  );
  const existingNecMemberIds = useMemo(
    () => necs.map((n: any) => n.member_id),
    [necs],
  );

  const usedCommitteeNames = useMemo(
    () => new Set(committees.map((c: Committee) => c.name)),
    [committees],
  );

  const regularCommittees = useMemo(
    () => committees.filter((c: Committee) => !PP_TYPE_SET.has(c.name)),
    [committees],
  );
  const ppCommittees = useMemo(
    () => committees.filter((c: Committee) => PP_TYPE_SET.has(c.name)),
    [committees],
  );

  // ── Committee helpers ────────────────────────────────────────────────────
  function cmtMemberName(m: any) {
    return (
      [m?.surname, m?.firstname, m?.othernames].filter(Boolean).join(" ") || "—"
    );
  }

  const handleCreateCommittee = async () => {
    if (!newCommittee.name.trim()) return;
    const isOpen = newCommittee.committee_type === "open";
    const payload = {
      setId,
      name: newCommittee.name.trim(),
      description: newCommittee.description.trim() || undefined,
      committee_type: newCommittee.committee_type,
      chairman_executive_id:
        !isOpen && newCommittee.chairman_executive_id
          ? Number(newCommittee.chairman_executive_id)
          : null,
    };
    try {
      const result = await createCommittee(payload).unwrap();
      if (!result?.id) {
        setMsg({
          type: "error",
          text:
            (result as any)?.message ||
            "Committee creation failed. Please try again.",
        });
        return;
      }
      setNewCommittee({
        name: "",
        description: "",
        committee_type: "executive_set",
        chairman_executive_id: "",
      });
      setShowCreateCommittee(false);
    } catch (e: any) {
      setMsg({
        type: "error",
        text: e?.data?.message || e?.error || "Failed to create committee.",
      });
    }
  };

  const handleCreatePpCommittee = async () => {
    if (!newPpCommittee.name.trim()) return;
    try {
      const result = await createCommittee({
        setId,
        name: newPpCommittee.name.trim(),
        description: newPpCommittee.description.trim() || undefined,
        chairman_executive_id: null,
      }).unwrap();
      if (!result?.id) {
        setMsg({
          type: "error",
          text: (result as any)?.message || "Committee creation failed.",
        });
        return;
      }
      setNewPpCommittee({ name: "", description: "" });
      setShowCreatePpCommittee(false);
    } catch (e: any) {
      setMsg({
        type: "error",
        text: e?.data?.message || e?.error || "Failed to create committee.",
      });
    }
  };

  const handleDeleteCommittee = async (committeeId: number) => {
    // Only empty committees offer the button now, so this no longer warns
    // about members going with it.
    if (!window.confirm("Delete this committee?")) return;
    try {
      await deleteCommittee({ committeeId, setId }).unwrap();
      if (expandedCommittee === committeeId) setExpandedCommittee(null);
      refetchCommittees();
    } catch (e: any) {
      setMsg({
        type: "error",
        text: e?.data?.message || "Failed to delete committee.",
      });
    }
  };

  const handleAddMember = async (committeeId: number) => {
    if (!selectedMember) return;
    try {
      await addMember({
        committeeId,
        setId,
        member_id: selectedMember.id,
        committee_role: newMemberRole,
        position: newMemberPosition.trim() || undefined,
      }).unwrap();
      setCommitteeMsg({
        id: committeeId,
        type: "success",
        text: `${selectedMember.label} added.`,
      });
      setSelectedMember(null);
      setMemberSearch("");
      setNewMemberRole("member");
      setNewMemberPosition("");
      setAddingMemberToCommittee(null);
      dispatch(
        apiSlice.util.invalidateTags([{ type: "CommitteeMembers", id: committeeId }])
      );
    } catch (e: any) {
      setCommitteeMsg({
        id: committeeId,
        type: "error",
        text: e?.data?.message || "Failed to add member.",
      });
    }
  };

  const handleRemoveMember = async (
    committeeId: number,
    memberId: string,
    name: string,
    action: "drop" | "delete",
  ) => {
    const confirmMsg =
      action === "delete"
        ? `Delete ${name}'s record entirely? Use this only if they were added in error.`
        : `Drop ${name} from this committee? Their record will be kept for reference.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await removeMember({ committeeId, memberId, setId, action }).unwrap();
      setCommitteeMsg({
        id: committeeId,
        type: "success",
        text:
          action === "delete"
            ? `${name}'s record deleted.`
            : `${name} dropped (record retained).`,
      });
    } catch (e: any) {
      setCommitteeMsg({
        id: committeeId,
        type: "error",
        text: e?.data?.message || "Failed to remove member.",
      });
    }
  };

  const roleBadge = (role: string) => {
    const map: Record<string, string> = {
      chairman: "bg-amber-100 text-amber-800",
      secretary: "bg-blue-100 text-blue-800",
      member: "bg-gray-100 text-gray-700",
    };
    return (
      <span
        className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
          map[role] ?? map.member
        }`}
      >
        {role}
      </span>
    );
  };

  // ===== Actions =====
  const handleUnassign = async (type: "exec" | "nec", id: number) => {
    if (
      !window.confirm(
        `Remove this member from the ${
          type === "exec" ? "Executive" : "NEC"
        } board?`,
      )
    )
      return;
    setMsg(null);
    try {
      type === "exec"
        ? await removeExec(id).unwrap()
        : await removeNec(id).unwrap();
      setMsg({ type: "success", text: "Member unassigned successfully." });
      refetch();
    } catch (e: any) {
      setMsg({ type: "error", text: e?.data?.message || "Operation failed" });
    }
  };

  const handleMove = async (
    type: "exec" | "nec",
    targetId: number,
    officeId: number,
  ) => {
    setMsg(null);
    try {
      // The server says whether the move granted any access — it doesn't on a
      // set that isn't the sitting one — so prefer its wording over ours.
      const res: any =
        type === "exec"
          ? await moveExec({
              executiveId: targetId,
              executive_office_id: officeId,
            }).unwrap()
          : await moveNec({ necId: targetId, nec_office_id: officeId }).unwrap();
      setMsg({ type: "success", text: res?.message || "Member position updated." });
      refetch();
    } catch (e: any) {
      setMsg({ type: "error", text: e?.data?.message || "Move failed" });
    }
  };

  if (isLoading)
    return (
      <div className="p-10 text-center animate-pulse text-gray-400">
        Synchronizing Term Records...
      </div>
    );

  return (
    <PageGate perm={PERMS.executive_set?.detailsAccess}>
      <div className="space-y-6 pb-20">
        {/* Header Card */}
        <div className="bg-white border rounded-xl p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gray-900 rounded-xl shadow-lg shadow-gray-200">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <button
                  onClick={() => navigate("/executive-sets")}
                  className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 mb-1"
                >
                  <ChevronLeft className="w-3 h-3" /> BACK TO TERMS
                </button>
                <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                  {data?.name}
                </h1>
                <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 font-medium">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" /> {data?.start_date}
                  </span>
                  <ArrowRightLeft className="w-3 h-3" />
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" /> {data?.end_date}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={() => refetch()}
              className="p-2.5 rounded-xl border bg-white hover:bg-gray-50 transition-all shadow-sm"
            >
              <RefreshCw
                className={`w-4 h-4 text-gray-600 ${
                  isLoading ? "animate-spin" : ""
                }`}
              />
            </button>
          </div>

          {msg && (
            <div
              className={`mt-6 p-4 rounded-xl border text-sm font-medium animate-in fade-in slide-in-from-top-2 ${
                msg.type === "success"
                  ? "bg-green-50 border-green-200 text-green-800"
                  : "bg-red-50 border-red-200 text-red-800"
              }`}
            >
              <div className="flex items-center gap-2">
                {msg.type === "success" ? (
                  <ShieldCheck className="w-4 h-4" />
                ) : (
                  <ShieldAlert className="w-4 h-4" />
                )}
                {msg.text}
              </div>
            </div>
          )}
        </div>

        {/* Board Sections */}
        <div className="grid grid-cols-1 gap-10">
          {/* Section: Executives */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-blue-50">
                  <Crown className="w-4 h-4 text-blue-600" />
                </div>
                <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest">
                  Executive Council ({executives.length})
                </h2>
              </div>
            </div>

            <div className="bg-white border rounded-2xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="border-b bg-gray-50/50 text-[10px] font-black uppercase tracking-widest text-gray-400">
                      <th className="py-4 px-6">Member Profile</th>
                      <th className="py-4 px-6">Assigned Office</th>
                      <th className="py-4 px-6">Contact Info</th>
                      <th className="py-4 px-6 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {executives.map((e: any) => (
                      <tr
                        key={e.id}
                        className="hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="py-4 px-6">
                          <div className="font-bold text-gray-900">
                            {fullName(e.member)}
                          </div>
                          <div className="text-[10px] text-gray-400 font-mono">
                            {e.member?.membership_no}
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <div className="text-blue-600 font-bold text-xs uppercase tracking-tighter">
                            {e.office?.name || "No Title"}
                          </div>
                          <div className="text-[10px] text-gray-400">
                            {e.position || "Principal Officer"}
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex flex-col gap-1">
                            <span className="flex items-center gap-1.5 text-xs text-gray-600">
                              <Mail className="w-3 h-3" /> {e.member?.email}
                            </span>
                            <span className="flex items-center gap-1.5 text-xs text-gray-600">
                              <Phone className="w-3 h-3" /> {e.member?.phone}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-6 text-right">
                          <div className="flex justify-end gap-2">
                            <AccessGate perm={PERMS.executive_set?.update}>
                              <select
                                className="text-[10px] font-bold border rounded-lg px-2 py-1 bg-white"
                                defaultValue={e.executive_office_id}
                                disabled={movingExec}
                                onChange={(ev) =>
                                  handleMove(
                                    "exec",
                                    e.id,
                                    Number(ev.target.value),
                                  )
                                }
                              >
                                {execOffices.map((o: any) => (
                                  <option key={o.id} value={o.id}>
                                    MOVE TO: {o.name}
                                  </option>
                                ))}
                              </select>
                              <button
                                onClick={() => handleUnassign("exec", e.id)}
                                disabled={removingExec}
                                className="p-2 border rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </AccessGate>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <AssignExecutiveToSet
              executiveSetId={setId}
              existingMemberIds={existingExecMemberIds}
              onSuccess={refetch}
            />
          </section>

          {/* ── Section: Committees ──────────────────────────────────── */}
          <section className="space-y-4">
            {/* Section header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-emerald-50">
                  <Building2 className="w-4 h-4 text-emerald-600" />
                </div>
                <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest">
                  Committees ({regularCommittees.length})
                </h2>
              </div>
              <AccessGate perm={PERMS.executive_set?.update}>
                <button
                  onClick={() => setShowCreateCommittee((v) => !v)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-bold hover:bg-gray-700 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  New Committee
                </button>
              </AccessGate>
            </div>

            {/* Create committee form */}
            {showCreateCommittee && (
              <div className="bg-white border rounded-2xl p-5 shadow-sm space-y-4">
                <h3 className="text-sm font-black text-gray-800 uppercase tracking-wider">
                  Create New Committee
                </h3>
                {/* Committee category (executive-set vs open) */}
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 block mb-2">
                    Committee Category *
                  </label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="committee_type"
                        value="executive_set"
                        checked={newCommittee.committee_type === "executive_set"}
                        onChange={() =>
                          setNewCommittee((p) => ({
                            ...p,
                            committee_type: "executive_set",
                          }))
                        }
                        className="accent-gray-800"
                      />
                      <span className="text-xs font-bold text-gray-700">
                        Executive-Set Committee
                      </span>
                      <span className="text-[10px] text-gray-400">
                        (chairman/secretary must be an executive)
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="committee_type"
                        value="open"
                        checked={newCommittee.committee_type === "open"}
                        onChange={() =>
                          setNewCommittee((p) => ({
                            ...p,
                            committee_type: "open",
                            chairman_executive_id: "",
                          }))
                        }
                        className="accent-gray-800"
                      />
                      <span className="text-xs font-bold text-gray-700">
                        Open Committee
                      </span>
                      <span className="text-[10px] text-gray-400">
                        (any member can chair)
                      </span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 block mb-1">
                      Committee Name *
                    </label>
                    <select
                      value={newCommittee.name}
                      onChange={(e) => {
                        const name = e.target.value;
                        const updates: Partial<typeof newCommittee> = { name };
                        if (name === "Examination Board") {
                          const chiefExaminer = executives.find(
                            (ex: any) => ex.office?.name === "Chief Examiner",
                          );
                          if (chiefExaminer)
                            updates.chairman_executive_id = chiefExaminer.id;
                        }
                        setNewCommittee((p) => ({ ...p, ...updates }));
                      }}
                      className="w-full border rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-gray-200"
                    >
                      <option value="">— Select committee type —</option>
                      {COMMITTEE_TYPES.filter(
                        (type) => !usedCommitteeNames.has(type),
                      ).map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  {newCommittee.committee_type === "executive_set" && (
                    <div>
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 block mb-1">
                        Chairman (optional)
                      </label>
                      <select
                        value={newCommittee.chairman_executive_id}
                        onChange={(e) =>
                          setNewCommittee((p) => ({
                            ...p,
                            chairman_executive_id: e.target.value,
                          }))
                        }
                        className="w-full border rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-gray-200"
                      >
                        <option value="">— Select an executive —</option>
                        {executives.map((e: any) => (
                          <option key={e.id} value={e.id}>
                            {fullName(e.member)} ({e.office?.name || "Executive"})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {newCommittee.committee_type === "open" && (
                    <div className="flex items-end">
                      <p className="text-[10px] text-blue-700 font-bold bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 w-full">
                        Chairman and Secretary can be any member — assign them after creating the committee.
                      </p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 block mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    rows={2}
                    value={newCommittee.description}
                    onChange={(e) =>
                      setNewCommittee((p) => ({
                        ...p,
                        description: e.target.value,
                      }))
                    }
                    placeholder="Brief description of this committee's mandate"
                    className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gray-200 resize-none"
                  />
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={handleCreateCommittee}
                    disabled={creatingCommittee || !newCommittee.name.trim()}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-xs font-black hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                  >
                    <Check className="w-3.5 h-3.5" />
                    {creatingCommittee ? "Creating…" : "Create Committee"}
                  </button>
                  <button
                    onClick={() => setShowCreateCommittee(false)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border text-xs font-bold text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" /> Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Committee cards */}
            {regularCommittees.length === 0 && !showCreateCommittee && (
              <div className="bg-white border rounded-2xl p-10 text-center text-gray-400 shadow-sm">
                <Building2 className="w-10 h-10 mx-auto mb-3 opacity-20" />
                <p className="text-sm">
                  No committees yet for this executive set.
                </p>
              </div>
            )}

            <div className="space-y-3">
              {regularCommittees.map((committee: Committee) => (
                <CommitteeCard
                  key={committee.id}
                  committee={committee}
                  setId={setId}
                  executives={executives}
                  expanded={expandedCommittee === committee.id}
                  onToggle={() =>
                    setExpandedCommittee((v) =>
                      v === committee.id ? null : committee.id,
                    )
                  }
                  onDelete={() => handleDeleteCommittee(committee.id)}
                  addingMemberOpen={addingMemberToCommittee === committee.id}
                  onToggleAddMember={() =>
                    setAddingMemberToCommittee((v) =>
                      v === committee.id ? null : committee.id,
                    )
                  }
                  memberSearch={memberSearch}
                  onMemberSearch={(q) => {
                    setMemberSearch(q);
                    if (q.length >= 3) searchMembers(q);
                  }}
                  memberResults={memberResults}
                  searchingMembers={searchingMembers}
                  selectedMember={selectedMember}
                  onSelectMember={setSelectedMember}
                  newMemberRole={newMemberRole}
                  onRoleChange={setNewMemberRole}
                  newMemberPosition={newMemberPosition}
                  onPositionChange={setNewMemberPosition}
                  onAddMember={() => handleAddMember(committee.id)}
                  addingMember={addingMember}
                  onRemoveMember={(memberId, name, action) =>
                    handleRemoveMember(committee.id, memberId, name, action)
                  }
                  committeeMsg={
                    committeeMsg?.id === committee.id ? committeeMsg : null
                  }
                  roleBadge={roleBadge}
                />
              ))}
            </div>
          </section>

          {/* ── Section: Past Presidents Committees ─────────────────── */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-amber-50">
                  <Crown className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest">
                    Past Presidents Committees ({ppCommittees.length})
                  </h2>
                  <p className="text-[10px] text-gray-400 font-medium mt-0.5">
                    Chairman &amp; Secretary elected from among past presidents
                  </p>
                </div>
              </div>
              <AccessGate perm={PERMS.executive_set?.update}>
                <button
                  onClick={() => setShowCreatePpCommittee((v) => !v)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-700 text-white text-xs font-bold hover:bg-amber-800 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  New Committee
                </button>
              </AccessGate>
            </div>

            {/* PP create form */}
            {showCreatePpCommittee && (
              <div className="bg-white border border-amber-100 rounded-2xl p-5 shadow-sm space-y-4">
                <h3 className="text-sm font-black text-gray-800 uppercase tracking-wider">
                  Create Past Presidents Committee
                </h3>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 block mb-1">
                    Committee Type *
                  </label>
                  <select
                    value={newPpCommittee.name}
                    onChange={(e) =>
                      setNewPpCommittee((p) => ({ ...p, name: e.target.value }))
                    }
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-amber-200"
                  >
                    <option value="">— Select committee type —</option>
                    {PAST_PRESIDENT_COMMITTEE_TYPES.filter(
                      (type) => !usedCommitteeNames.has(type),
                    ).map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 block mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    rows={2}
                    value={newPpCommittee.description}
                    onChange={(e) =>
                      setNewPpCommittee((p) => ({
                        ...p,
                        description: e.target.value,
                      }))
                    }
                    placeholder="Brief description of this committee's mandate"
                    className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-200 resize-none"
                  />
                </div>
                <p className="text-[10px] text-amber-700 font-bold bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  Chairman and Secretary will be assigned from among Past Presidents when adding members.
                </p>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={handleCreatePpCommittee}
                    disabled={creatingCommittee || !newPpCommittee.name.trim()}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-700 text-white text-xs font-black hover:bg-amber-800 disabled:opacity-50 transition-colors"
                  >
                    <Check className="w-3.5 h-3.5" />
                    {creatingCommittee ? "Creating…" : "Create Committee"}
                  </button>
                  <button
                    onClick={() => setShowCreatePpCommittee(false)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border text-xs font-bold text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" /> Cancel
                  </button>
                </div>
              </div>
            )}

            {ppCommittees.length === 0 && !showCreatePpCommittee && (
              <div className="bg-white border border-dashed border-amber-200 rounded-2xl p-10 text-center text-gray-400 shadow-sm">
                <Crown className="w-10 h-10 mx-auto mb-3 text-amber-200" />
                <p className="text-sm">
                  No past presidents committees yet for this set.
                </p>
              </div>
            )}

            <div className="space-y-3">
              {ppCommittees.map((committee: Committee) => (
                <CommitteeCard
                  key={committee.id}
                  committee={committee}
                  setId={setId}
                  executives={executives}
                  expanded={expandedCommittee === committee.id}
                  onToggle={() =>
                    setExpandedCommittee((v) =>
                      v === committee.id ? null : committee.id,
                    )
                  }
                  onDelete={() => handleDeleteCommittee(committee.id)}
                  addingMemberOpen={addingMemberToCommittee === committee.id}
                  onToggleAddMember={() =>
                    setAddingMemberToCommittee((v) =>
                      v === committee.id ? null : committee.id,
                    )
                  }
                  memberSearch={memberSearch}
                  onMemberSearch={(q) => {
                    setMemberSearch(q);
                    if (q.length >= 3) searchMembers(q);
                  }}
                  memberResults={memberResults}
                  searchingMembers={searchingMembers}
                  selectedMember={selectedMember}
                  onSelectMember={setSelectedMember}
                  newMemberRole={newMemberRole}
                  onRoleChange={setNewMemberRole}
                  newMemberPosition={newMemberPosition}
                  onPositionChange={setNewMemberPosition}
                  onAddMember={() => handleAddMember(committee.id)}
                  addingMember={addingMember}
                  onRemoveMember={(memberId, name, action) =>
                    handleRemoveMember(committee.id, memberId, name, action)
                  }
                  committeeMsg={
                    committeeMsg?.id === committee.id ? committeeMsg : null
                  }
                  roleBadge={roleBadge}
                />
              ))}
            </div>
          </section>
        </div>
      </div>
    </PageGate>
  );
}
